import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Collapse, Descriptions, Empty, List, Skeleton, Space, Tag, Typography } from "antd";
import { makeRequestStateless } from "@utils/helpers";
import { getPreviewReq } from "@mods/common/requests";
import { attendancePlannerPolicyDependencies } from "@mods/policyManagement/attendancePolicyPlanner/policyDependencyConfig";
import { getSalaryPolicyByEmpId } from "../requests";
import "./Employee.css";

const { Paragraph, Text, Title } = Typography;

const policySubject = {
  absentPolicy: "absence",
  missingPolicy: "missing attendance",
  lateArrivalPolicy: "late arrival",
  earlyLeftPolicy: "early leaving",
  shortDurationPolicy: "short working time",
  relaxationPolicy: "relaxation",
  leavePolicy: "deductible leave",
  overtimePolicy: "approved overtime",
  cplPolicy: "compensatory leave",
};

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null || amount === "") {
    return null;
  }

  const value = Number(amount);
  if (Number.isNaN(value)) {
    return amount;
  }

  return `Rs ${new Intl.NumberFormat("en-PK", { maximumFractionDigits: 2 }).format(value)}`;
};

const getName = (item, fallback = "Selected salary effect") => item?.name || item?.title || item?.code || fallback;

const titleCase = (value = "") =>
  value
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getPlannerRecord = (salaryPolicyData) => {
  if (salaryPolicyData?.policy?.policy_planer?.id) {
    return salaryPolicyData.policy.policy_planer;
  }

  if (salaryPolicyData?.policy?.policy_planer_id) {
    return {
      id: salaryPolicyData.policy.policy_planer_id,
      name: salaryPolicyData.policy.name,
    };
  }

  return null;
};

const findById = (items, id) => (items || []).find((item) => `${item.id}` === `${id}`);

const getRangeText = (slab, dependency) => {
  if (dependency?.key === "overtimePolicy" || dependency?.key === "cplPolicy") {
    if (slab?.min !== undefined && slab?.max !== undefined && slab?.max !== null) {
      return `${slab.min} to ${slab.max} approved minutes`;
    }

    if (slab?.min !== undefined && slab?.min !== null) {
      return `${slab.min}+ approved minutes`;
    }

    return "approved minutes";
  }

  const minutes = slab?.end_mins
    ? `${slab.start_mins} to ${slab.end_mins} minutes`
    : slab?.start_mins
      ? `${slab.start_mins}+ minutes`
      : null;

  const occurrences = slab?.max_times
    ? `occurrence ${slab.min_times} to ${slab.max_times}`
    : slab?.min_times
      ? `occurrence ${slab.min_times}+`
      : null;

  return [minutes, occurrences].filter(Boolean).join(", ") || "matching attendance";
};

const getEffectText = (slab, dependency, relatedData) => {
  const impact = slab?.impact_preview;
  const isOvertime = dependency?.key === "overtimePolicy";
  const isCpl = dependency?.key === "cplPolicy";
  const earningType = findById(relatedData?.encashment_types, slab?.encashment_type_id);
  const deductionType = findById(relatedData?.deduction_types, slab?.deduction_type_id);
  const fallback = findById(relatedData?.deduction_types, slab?.fallback_type_id)
    || findById(relatedData?.encashment_types, slab?.fallback_type_id);

  if (impact?.message) {
    return impact.message;
  }

  if (isOvertime) {
    return `Payroll pays overtime using ${getName(earningType || slab?.encashment_slab, "the selected earning effect")}.`;
  }

  if (isCpl) {
    return `The system adds compensatory leave using ${getName(earningType || slab?.encashment_slab, "the selected CPL effect")}.`;
  }

  const main = `Payroll deducts or adjusts salary using ${getName(deductionType || slab?.deduction_slab, "the selected deduction effect")}.`;
  return fallback
    ? `${main} If quota is not enough, fallback uses ${getName(fallback)}.`
    : main;
};

const amountTagColor = (effectType) => effectType === "earning" ? "green" : "red";

const EmployeePolicyImpact = ({ dependency, policy, relatedData }) => {
  const slabs = policy?.slabs || [];

  return (
    <Card className="employee-policy-impact__rule" size="small">
      <Space direction="vertical" size={ 12 } style={ { width: "100%" } }>
        <div className="employee-policy-impact__rule-header">
          <div>
            <Text strong>{ policy?.name || dependency.label }</Text>
            <Paragraph type="secondary">
              This rule is checked during payroll when { policySubject[dependency.key] || dependency.label.toLowerCase() } is found in attendance.
            </Paragraph>
          </div>
          <Tag color="processing">{ slabs.length } slab{ slabs.length === 1 ? "" : "s" }</Tag>
        </div>

        { policy?.description ? <Alert type="info" showIcon message={ policy.description } /> : null }

        { slabs.length ? (
          <List
            size="small"
            dataSource={ slabs }
            renderItem={ (slab, index) => (
              <List.Item>
                <Space direction="vertical" size={ 4 }>
                  <Text strong>Slab { index + 1 }: { getRangeText(slab, dependency) }</Text>
                  { slab?.impact_preview?.formatted_amount ? (
                    <Space wrap size={ [8, 4] }>
                      <Tag color={ amountTagColor(slab.impact_preview.effect_type) }>
                        { slab.impact_preview.effect_type === "earning" ? "Approx earning" : "Approx deduction" }: { slab.impact_preview.formatted_amount }
                      </Tag>
                      { slab.impact_preview.formula_name ? <Tag color="blue">Formula: { slab.impact_preview.formula_name }</Tag> : null }
                      { slab.impact_preview.effect_method ? <Tag>{ slab.impact_preview.effect_method }</Tag> : null }
                    </Space>
                  ) : (
                    <Tag color="default">Amount will show after formula setup is complete</Tag>
                  ) }
                  <Text>{ getEffectText(slab, dependency, relatedData) }</Text>
                  { slab?.impact_preview?.formatted_fallback_amount ? (
                    <Text type="secondary">Fallback amount: { slab.impact_preview.formatted_fallback_amount }.</Text>
                  ) : null }
                  { slab?.repeat_name ? <Text type="secondary">Repeat rule: { slab.repeat_name }.</Text> : null }
                </Space>
              </List.Item>
            ) }
          />
        ) : (
          <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No slab rules are configured for this policy." />
        ) }
      </Space>
    </Card>
  );
};

const EmployeePoliciesTab = ({ employmentId, compact = false, context = "profile" }) => {
  const isPayslipContext = context === "payslip";
  const [activePanelKeys, setActivePanelKeys] = useState([]);
  const [loading, setLoading] = useState(!isPayslipContext);
  const [salary, setSalary] = useState(null);
  const [planner, setPlanner] = useState(null);
  const [relatedData, setRelatedData] = useState({});
  const [error, setError] = useState(null);
  const shouldLoadPolicies = !isPayslipContext || activePanelKeys.includes("policy-impact");

  useEffect(() => {
    setError(null);
    setSalary(null);
    setPlanner(null);
    setRelatedData({});

    if (!employmentId) {
      setLoading(false);
      return;
    }

    if (!shouldLoadPolicies) {
      setLoading(false);
      return;
    }

    setLoading(true);

    makeRequestStateless(
      getSalaryPolicyByEmpId,
      employmentId,
      (response) => {
        setSalary(response?.salary || null);
        const plannerRecord = getPlannerRecord(response);

        if (!plannerRecord?.id) {
          setLoading(false);
          return;
        }

        makeRequestStateless(
          getPreviewReq,
          { moduleName: "policyPlaners", policy_planer: { id: plannerRecord.id, employment_id: employmentId } },
          (previewResponse) => {
            setPlanner(previewResponse?.policy_planer || null);
            setRelatedData(previewResponse || {});
            setLoading(false);
          },
          () => {
            setError("Unable to load assigned policy details.");
            setLoading(false);
          }
        );
      },
      () => {
        setError("Unable to load employee salary and policy information.");
        setLoading(false);
      }
    );
  }, [employmentId, shouldLoadPolicies]);

  const assignedPolicies = useMemo(() => {
    if (!planner) {
      return [];
    }

    return attendancePlannerPolicyDependencies
      .map((dependency) => ({
        dependency,
        policy: planner[dependency.previewKey],
      }))
      .filter((item) => item.policy?.id);
  }, [planner]);

  const introText = context === "attendance"
    ? "These rules explain how your attendance can affect payroll. Final amounts are confirmed when payroll is processed."
    : context === "payslip"
      ? "These assigned rules explain why attendance-related earnings or deductions can appear on your payslip."
      : "These are the attendance policies currently assigned to this employee and how they can affect payroll.";

  const content = (
    <div className={ `employee-policy-impact${compact ? " employee-policy-impact--compact" : ""}` }>
      <Skeleton loading={ loading } active paragraph={ { rows: compact ? 4 : 8 } }>
        { error ? (
          <Alert type="error" showIcon message={ error } />
        ) : null }

        { !error && !planner ? (
          <Empty
            description="No attendance payroll policy is assigned yet."
            image={ Empty.PRESENTED_IMAGE_SIMPLE }
          />
        ) : null }

        { !error && planner ? (
          <Space direction="vertical" size={ 14 } style={ { width: "100%" } }>
            <div className="employee-policy-impact__header">
              <div>
                <Title level={ compact ? 5 : 4 }>Payroll policy impact</Title>
                <Paragraph type="secondary">{ introText }</Paragraph>
              </div>
              <Space wrap>
                { salary?.type ? <Tag color="blue">{ titleCase(salary.type) }</Tag> : null }
                { salary?.gross_salary ? <Tag color="green">Gross salary: { formatCurrency(salary.gross_salary) }</Tag> : null }
                <Tag color="purple">{ assignedPolicies.length } rule{ assignedPolicies.length === 1 ? "" : "s" }</Tag>
              </Space>
            </div>

            <Descriptions size="small" column={ compact ? 1 : 2 } bordered>
              <Descriptions.Item label="Policy package">{ planner?.name || "Policy package" }</Descriptions.Item>
              <Descriptions.Item label="Effective from">{ planner?.date || "Not available" }</Descriptions.Item>
              <Descriptions.Item label="Assigned through">{ planner?.for_all ? "All employees" : "Selected employee mapping" }</Descriptions.Item>
              <Descriptions.Item label="Salary reference">{ formatCurrency(salary?.gross_salary) || "Not available" }</Descriptions.Item>
            </Descriptions>

            { assignedPolicies.length ? (
              assignedPolicies.map(({ dependency, policy }) => (
                <EmployeePolicyImpact
                  key={ dependency.key }
                  dependency={ dependency }
                  policy={ policy }
                  relatedData={ relatedData }
                />
              ))
            ) : (
              <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="This package has no attendance rules attached." />
            ) }
          </Space>
        ) : null }
      </Skeleton>
    </div>
  );

  if (isPayslipContext) {
    return (
      <Collapse
        className="employee-policy-impact-collapse"
        activeKey={ activePanelKeys }
        onChange={ (keys) => setActivePanelKeys(Array.isArray(keys) ? keys : [keys].filter(Boolean)) }
        items={ [
          {
            key: "policy-impact",
            label: (
              <Space wrap size={ [8, 4] }>
                <Text strong>Payroll policy impact</Text>
                <Text type="secondary">Open to see attendance rules, slabs, and approximate earning or deduction amounts.</Text>
              </Space>
            ),
            children: content,
          }
        ] }
      />
    );
  }

  return content;
};

export default EmployeePoliciesTab;
